import * as React from "react";
import { type UUID, createUuid } from "../utils/uuid";
import { ElofleetList, type ElofleetListProps } from "./atoms/elofleet-list";
import { observer } from "mobx-react";
import { action, computed, makeObservable } from "mobx";
import {
    type VehicleTypesApiResource,
    type VehicleTypesQuery,
} from "../domain/repositories/repository-vehicle-types";
import { defaultPageSize } from "../utils/constants";
import type { VehicleTypeSortKey, VehicleType } from "../api";
import type { SortState } from "../utils/sort-state";
import { LayoutList } from "./layouts/layout-list";
import type { PaginationState } from "../utils/pagination-state";
import type { SelectionState } from "../utils/selection-state";
import { type IObjectWithKey, Selection } from "@fluentui/react";
import { omit } from "ramda";
import { store } from "../store";

export interface ListVehicleTypesProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface ListVehicleTypeItem extends IObjectWithKey {
    readonly label: string;
    readonly key: UUID;
}

@observer
export class ListVehicleTypes extends React.Component<ListVehicleTypesProps> {
    private _listStateId = createUuid();
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    constructor(props: ListVehicleTypesProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.vehicleTypes.initializeList(this.listStateId, {
            query: () => this.query,
        });
    }

    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<VehicleTypeSortKey> {
        return store.serviceListStates.vehicleTypes.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<VehicleTypesApiResource> {
        return store.serviceListStates.vehicleTypes.getPaginationState(this.listStateId);
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.vehicleTypes.getSelectionState(this.listStateId);
    }

    @computed private get query(): VehicleTypesQuery {
        return { pageSize: defaultPageSize, ...this.sortState.query };
    }

    @computed private get vehicleTypes(): (VehicleType | undefined)[] {
        return store.repositoryVehicleTypes.byQuery(this.query, this.paginationState.pagination);
    }

    @computed private get items(): (ListVehicleTypeItem | undefined)[] {
        return this.vehicleTypes.map((site) => {
            if (!site) {
                return;
            }
            return {
                label: site.label,
                key: site.id,
            };
        });
    }

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.vehicleTypes.isInitialized(this.listStateId)) {
            return <></>;
        }

        const props = omit(["ids"], this.props);
        return (
            <LayoutList paginationState={this.paginationState} commandBar={this.props.commandBar}>
                <ElofleetList
                    {...props}
                    selection={this.selection}
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.sortState.patchColumns([
                        {
                            fieldName: "label",
                            name: store.i18n.t("listVehicleTypes.column.label.name"),
                            key: "label",
                            minWidth: 100,
                        },
                    ])}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
